// LSP-19055: a user-initiated organization switch whose silent refresh-token grant fails ends in
// auth.setOrganizationId's full-document loginWithRedirect fallback. That redirect destroys the
// Vue application, so the only user-facing failure channel on that path —
// SwitchOrganizationDialog's `toast.error`, App.vue's `switchToOrg` toast, and
// notifications.openOrgSwitchFailedDialog — is unreachable by construction:
// _redirectToAuth0AndBlockUntilNavigation deliberately never settles on a successful handoff
// (`await new Promise(() => {})`), so the line after the await never runs, and the component
// holding it is torn down by the navigation regardless.
//
// The fix is a marker that outlives the document: record the requested organization BEFORE handing
// navigation to Auth0, then adjudicate it on the next boot against the organization we actually
// landed in. Feedback for a failure that destroys the page has to survive the page.
//
// sessionStorage (not localStorage) because the marker belongs to ONE tab's navigation: the redirect
// leaves and returns in the same tab, and an unrelated tab opened separately must not inherit a
// pending switch it never requested — localStorage would warn in every open tab.
//
// KNOWN LIMITATION, deliberately accepted: sessionStorage is COPIED into an auxiliary browsing
// context created from this one, so duplicating the tab (or opening it via an opener) while the
// redirect is in flight hands the copy a marker it did not request, and that copy can show one
// spurious warning. Do not "fix" this by reaching for the per-tab owner id in db/fileUploadDb.ts —
// that id is sessionStorage-backed too and is copied by exactly the same mechanism (see its own
// comment about duplicated tabs). Reaching this needs the user to duplicate a tab during the
// sub-second window in which it is frozen mid-navigation, and the cost is one dismissible warning;
// closing it would need a per-document token that survives a real navigation but not a duplication,
// which the platform does not offer.
//
// SECOND KNOWN LIMITATION: in a storage-restricted context (private mode, locked-down embed) the
// write below fails and this whole mechanism degrades to the pre-fix behaviour — the switch still
// happens, the user still gets no notice. That is logged rather than silent, which is the most that
// is available without a storage channel.

import { logInfo, logWarn } from "./logError";

export const PENDING_ORG_SWITCH_STORAGE_KEY = "contact:pendingOrgSwitch";

// Routes that are themselves auth transit: App.vue mounts on them (main.ts mounts it for every path
// except /form, /account-setup and /free-trial), but they exist only to hand navigation straight back
// to Auth0, so anything shown there is torn down before it can be read. Adjudicating on one of these
// would burn the marker to display a toast nobody sees — see the deferral in consumePendingOrgSwitch.
//   /callback  the code/state exchange; the organization is still mid-flight and navigates to "/".
//   /logout    the post-sign-out landing, which re-enters Auth0 immediately (break-it round 3).
// Compared against window.location.pathname rather than the router's route name: main.ts mounts
// App.vue without awaiting router.isReady(), so `$route.name` can still be undefined in mounted()
// while the pathname is always authoritative.
const AUTH_TRANSIT_PATHS = ["/callback", "/logout"];

// Organization ids in this app are Auth0/API UUIDs. The bound is not about trusting our own writer —
// it is about what a page-scoped attacker or a corrupted storage entry can push through this
// function: an id long enough to be logged to Datadog as a multi-megabyte attribute, or one that
// differs from the real id only by surrounding whitespace and would therefore be classified as "a
// DIFFERENT organization" and manufacture a warning. Both are false-positive vectors, so both are
// rejected here rather than at each call site. Break-it round 1.
const MAX_ORGANIZATION_ID_LENGTH = 128;

// Read-once is a promise this module makes; sessionStorage.removeItem is allowed to fail. Without an
// in-memory latch a failing removeItem silently downgrades read-once to read-every-time, so one
// failed switch could warn on every consumer for the life of the document. Reset by
// markOrgSwitchHandedToRedirect, because a new switch in the same document is a new notice.
let consumedInThisDocument = false;

// A fresh id per marker WRITE, so "is this notice still the one I seeded / read?" has an exact answer.
// Rounds 4-6 keyed that question on the organization, and round 7 showed why that is not enough: two
// distinct explicit attempts can target the SAME organization, and then an older attempt's abandon
// clears the newer attempt's notice, or an older deferred verdict spends it. The organization cannot
// distinguish attempts; only a per-write identity can. Unique per write and per document, because a
// marker seeded in one document is read back in the next one and a counter alone would collide.
let fallbackIdCounter = 0;

function newPendingOrgSwitchId(): string {
	try {
		if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
			return crypto.randomUUID();
		}
	} catch {
		// Some locked-down contexts throw on crypto access; fall through to the non-crypto id. This id
		// is an equality token for our own writes, never a security value, so a weaker source is fine.
	}
	// The counter, not the clock or the random sample, is what makes this unique: two writes in the
	// same millisecond that happened to draw the same random value would otherwise get identical ids,
	// and an older attempt could then pass the exact-id check and delete a newer attempt's notice
	// (break-it round 8). A per-document counter is sufficient because every ownership comparison
	// happens within one document.
	fallbackIdCounter += 1;
	return `${Date.now()}-${fallbackIdCounter}-${Math.random().toString(36).slice(2)}`;
}

// Normalizes to the canonical id, or null for "not a usable organization id". Trimming matters in
// both directions: `" org-1"` in storage must not read as a different organization from `"org-1"` in
// the token, and a whitespace-only current organization must count as UNREADABLE rather than as some
// other organization.
export function normalizeOrganizationId(value: unknown): string | null {
	if (typeof value !== "string") {
		return null;
	}
	const trimmed = value.trim();
	if (!trimmed || trimmed.length > MAX_ORGANIZATION_ID_LENGTH) {
		return null;
	}
	return trimmed;
}

export type PendingOrgSwitchOutcome =
	// No switch was handed to a redirect in this tab.
	| { outcome: "none" }
	// We are on the Auth0 callback document, where the organization is still mid-exchange. The
	// marker is left in place for the next document to adjudicate.
	| { outcome: "deferred" }
	// The redirect landed in the organization the user asked for. Nothing to tell them.
	| { outcome: "switched"; organizationId: string }
	// There is no readable organization at boot, so we cannot say whether the switch took effect.
	// Carries `pendingOrgSwitchId` because this is the one outcome whose caller holds a verdict open and
	// must later prove the notice it acts on is still the same one (break-it round 7).
	| { outcome: "unknown"; organizationId: string; pendingOrgSwitchId: string }
	// We are in a DIFFERENT organization than the one the user asked for: the switch did not happen.
	| { outcome: "failed"; organizationId: string };

// Returns the requested organization, or null for "there is nothing here I can act on" — which
// covers an absent marker, sessionStorage being unavailable (private mode, locked-down embeds), and
// a stored value that will not parse or carries no organization id. All four get the same answer
// because the caller's response is the same in all four: discard, so unreadable state is not
// re-parsed on every boot.
type StoredMarker = { organizationId: string; pendingOrgSwitchId: string };

function readStoredMarker(): StoredMarker | null {
	if (consumedInThisDocument) {
		// Already adjudicated in this document. Checked before touching storage so a removeItem that
		// failed cannot resurrect the marker.
		return null;
	}
	try {
		const raw = sessionStorage.getItem(PENDING_ORG_SWITCH_STORAGE_KEY);
		if (!raw) {
			return null;
		}
		const parsed = JSON.parse(raw);
		const organizationId = normalizeOrganizationId(parsed?.organizationId);
		if (!organizationId) {
			return null;
		}
		// An id is REQUIRED, with no "legacy marker" carve-out. A marker without one is not something any
		// version of this code writes — PENDING_ORG_SWITCH_STORAGE_KEY is introduced by this change, so
		// no earlier build ever populated it — which means the idless state was pure speculation, and it
		// carried a real cost: App.vue armed the empty id and then discarded its own verdict on the
		// truthiness check, so the notice went unshown (break-it round 8). Treating it as unreadable is
		// both simpler and how every other malformed shape here is already handled.
		const pendingOrgSwitchId = normalizeOrganizationId(parsed?.pendingOrgSwitchId);
		if (!pendingOrgSwitchId) {
			return null;
		}
		return { organizationId, pendingOrgSwitchId };
	} catch {
		return null;
	}
}

// Case-INSENSITIVE, matching the router rather than the string. Vue Router builds its path matcher as
// `new RegExp(pattern, options.sensitive ? "" : "i")` and defaults `sensitive: false`, so `/Callback`
// resolves to the callback route just as `/callback` does. A case-sensitive check here therefore
// disagreed with the router about whether this document is auth transit, and adjudicated on a document
// that was about to navigate — burning the marker to show a toast nobody could read (break-it round 4,
// which corrected round 1's assertion that `/Callback` was an ordinary document).
function isAuthTransitDocument(): boolean {
	const pathname = window.location.pathname.toLowerCase();
	return AUTH_TRANSIT_PATHS.some((transitPath) => pathname === transitPath || pathname === `${transitPath}/`);
}

/**
 * The identity of the pending notice, or null if none is pending.
 *
 * Deliberately exposes the exact per-write id and NOT a `hasPendingOrgSwitch()` boolean or the
 * organization. Both weaker forms shipped here and both were wrong: presence let App.vue's deferred
 * verdict assume a marker was its own and spend a newer switch's notice (round 6), and the
 * organization could not tell two attempts at the SAME organization apart (round 7). Every consumer
 * has to prove exact identity, so exact identity is the only question this module lets it ask.
 */
export function readPendingOrgSwitchId(): string | null {
	return readStoredMarker()?.pendingOrgSwitchId ?? null;
}

/**
 * Record that an organization switch is about to be handed to a full-document Auth0 redirect.
 * Called from auth.setOrganizationId immediately before the handoff, and cleared again if the
 * redirect does not actually happen (suppressed or failed) — so a surviving marker always means
 * "this document really did navigate away mid-switch".
 */
export function markOrgSwitchHandedToRedirect(organizationId: string): string | null {
	const normalizedOrganizationId = normalizeOrganizationId(organizationId);
	if (!normalizedOrganizationId) {
		// Nothing to adjudicate against on the next boot, so a marker here could only produce a
		// warning we cannot justify. Log it: reaching this means a caller lost the organization id.
		logWarn("pendingOrgSwitch: refused to mark a pending organization switch with no usable organization id", {});
		return null;
	}
	const pendingOrgSwitchId = newPendingOrgSwitchId();
	try {
		sessionStorage.setItem(
			PENDING_ORG_SWITCH_STORAGE_KEY,
			JSON.stringify({ organizationId: normalizedOrganizationId, pendingOrgSwitchId })
		);
	} catch (error) {
		// Losing the marker costs the user the failure notice, not the switch itself — so degrade
		// rather than throw into the middle of a redirect handoff. Logged so the degradation is
		// visible instead of looking like the original silent failure this fixes.
		logWarn("pendingOrgSwitch: could not persist the pending organization switch marker", {
			organizationId: normalizedOrganizationId,
			reason: error instanceof Error ? error.message : String(error)
		});
		return null;
	}
	// AFTER the write, never before (break-it round 7). A new switch in this document does supersede an
	// earlier verdict — but only once its own marker is actually stored. Resetting first meant that a
	// write which then FAILED, over a previous marker that could not be removed, republished the spent
	// old marker as live and warned about obsolete intent.
	consumedInThisDocument = false;
	return pendingOrgSwitchId;
}

function dropMarker(): void {
	// The in-memory latch is set here rather than only at the adjudication site because removeItem is
	// allowed to fail: without it, a failed removal on the ABANDON path left the marker readable, so the
	// caller's own dialog fired now AND the persisted warning fired on the next adjudication — the same
	// failure reported twice, which AC4 forbids (break-it round 3).
	consumedInThisDocument = true;
	try {
		sessionStorage.removeItem(PENDING_ORG_SWITCH_STORAGE_KEY);
	} catch {
		// sessionStorage may be unavailable in some contexts. The latch above still holds.
	}
}

/**
 * Drops whatever notice is pending, whoever seeded it. For the callers that retire the notice as a
 * FACT about the app rather than about one switch: it has been adjudicated, the user signed out, or a
 * switch actually succeeded and so superseded any older intent.
 */
export function clearPendingOrgSwitch(): void {
	dropMarker();
}

/**
 * Drops the notice only if it is the one `organizationId` seeded. For a caller abandoning ITS OWN
 * switch.
 *
 * The marker is a single slot, so it is not enough to know that a switch is giving up — you have to
 * know whether the pending notice is still that switch's. Two explicit switches can be in flight at
 * once: the newer one overwrites the marker with its own organization and freezes on its redirect,
 * and if the older one's redirect then rejects, an unconditional clear deletes the NEWER switch's
 * notice. The older caller's own toast still fires (its document survived), but the newer switch —
 * the one whose page is actually being replaced — loses the only channel it had left, which is the
 * silence this whole ticket is about (break-it round 5).
 */
export function clearPendingOrgSwitchFor(pendingOrgSwitchId: string | null | undefined): void {
	if (!pendingOrgSwitchId) {
		// The caller never got an id, so it never successfully seeded a notice and has nothing to drop.
		return;
	}
	const marker = readStoredMarker();
	if (!marker) {
		return;
	}
	if (marker.pendingOrgSwitchId !== pendingOrgSwitchId) {
		logWarn("pendingOrgSwitch: kept a pending notice that belongs to a different switch attempt", {
			abandonedPendingOrgSwitchId: pendingOrgSwitchId,
			pendingOrganizationId: marker.organizationId
		});
		return;
	}
	dropMarker();
}

/**
 * Read-once adjudication of a pending organization switch.
 *
 * Read-once is what bounds staleness: the marker is written only just before a real navigation and
 * consumed by the first document that can JUDGE the outcome, so an adjudicated notice cannot resurface
 * later. The two outcomes that judge nothing — an auth-transit document (AUTH_TRANSIT_PATHS) and an
 * unreadable organization — deliberately leave the marker for whoever can judge it; obsolete intent is
 * retired separately, by clearPendingOrgSwitch on every switch that actually succeeds and on sign-out.
 *
 * @param currentOrganizationId the organization this document actually booted into, from the
 *        authSession store (i.e. the token's `organization_id` claim). Null/empty means unreadable.
 */
export function consumePendingOrgSwitch(currentOrganizationId: string | null | undefined): PendingOrgSwitchOutcome {
	const marker = readStoredMarker();
	if (!marker) {
		// Also covers the unreadable/blank-marker case: discard it so it is not re-parsed forever.
		clearPendingOrgSwitch();
		return { outcome: "none" };
	}
	const requestedOrganizationId = marker.organizationId;

	if (isAuthTransitDocument()) {
		// On /callback the code/state exchange is still in flight and this document navigates to "/" as
		// soon as it completes (router/index.js callback branch); on /logout the guard re-enters Auth0
		// immediately. Consuming on either would read an organization that is still moving, fire a
		// warning nobody can read, AND burn the marker so the next document could not report a genuine
		// failure. Leave it for that document.
		//
		// The terminal callback exits (blocked user, the permanent metadata-size access_denied) keep
		// this document parked on /callback showing BlockedUser.vue, so the marker simply waits there
		// with nothing to say — the user cannot act on an organization switch until they get back in,
		// and when they do, that recovery loads a fresh document which adjudicates it. Warning them
		// about a switch on top of "we can't get you into your account" would be noise.
		logInfo("pendingOrgSwitch: deferred adjudication of a pending organization switch on an auth-transit document", {
			requestedOrganizationId,
			pathname: window.location.pathname
		});
		return { outcome: "deferred" };
	}

	const currentOrganization = normalizeOrganizationId(currentOrganizationId);
	if (!currentOrganization) {
		// Deliberately NOT reported as a failure. A boot with no readable organization is a broken
		// session, not a rejected switch — the session-lost banner and the router's login redirect
		// own that state, and asserting "we could not switch you" here would be a guess. Logged so
		// the un-adjudicated case is countable in Datadog rather than invisible.
		//
		// The DURABLE marker is deliberately left in place, like the auth-transit deferral above: this
		// outcome resolves nothing, so consuming it would move the only remaining record of the notice
		// into this document's memory, and a document that then navigates away (an unauthenticated boot
		// that redirects to login, a share-claim page that starts its own full-document sign-in) loses
		// it for good — the original silence, reintroduced (break-it round 3).
		//
		// Leaving it is safe against staleness because obsolete intent is now retired at the source:
		// any EXPLICIT switch that actually succeeds clears the marker (auth.js, the "switched" and
		// "already-current" exits), as does sign-out. So a surviving marker means the user asked to be
		// somewhere and has not been there since.
		//
		// The unreadable organization is usually transient (a boot token read that failed and succeeds
		// on the next refresh), so the caller ALSO holds `organizationId` in memory and re-adjudicates
		// the moment the organization becomes readable in this document — see App.vue
		// readjudicatePendingOrgSwitchIfArmed, which clears the marker when it resolves. That is why
		// this outcome carries the id at all.
		logWarn("pendingOrgSwitch: could not adjudicate a pending organization switch — no readable organization at boot", {
			requestedOrganizationId
		});
		return { outcome: "unknown", organizationId: requestedOrganizationId, pendingOrgSwitchId: marker.pendingOrgSwitchId };
	}

	// From here the outcome is adjudicated, so the notice is spent. clearPendingOrgSwitch also sets the
	// in-document latch, which is what keeps this read-once when removeItem is refused.
	clearPendingOrgSwitch();

	if (currentOrganization === requestedOrganizationId) {
		logInfo("pendingOrgSwitch: an organization switch completed through the full-page redirect fallback", {
			requestedOrganizationId
		});
		return { outcome: "switched", organizationId: requestedOrganizationId };
	}

	logWarn("pendingOrgSwitch: an organization switch that was handed to a full-page redirect did not take effect", {
		requestedOrganizationId,
		currentOrganizationId: currentOrganization
	});
	return { outcome: "failed", organizationId: requestedOrganizationId };
}

// Mirrors useOrgSync's _resetOrgSync: the read-once latch is document-scoped module state, and a
// test file that exercises several documents in one module instance needs to start each clean.
export function _resetPendingOrgSwitch(): void {
	consumedInThisDocument = false;
}
